import React, { useState, useEffect } from 'react';
import baseUrl from '../../config';

// Utility function to format dates (e.g., "Wed, Jun 12, 2025")
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const options = { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' };
  try {
    return new Date(dateString).toLocaleDateString(undefined, options);
  } catch (e) {
    console.error("Error formatting date:", dateString, e);
    return 'Invalid Date';
  }
};

// Utility function to format time (e.g., "02:30 PM")
const formatTime = (timeString) => {
  if (!timeString) return 'N/A';
  try {
    // Assuming timeString is in HH:MM format (e.g., "14:30")
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours, 10));
    date.setMinutes(parseInt(minutes, 10));
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    console.error("Error formatting time:", timeString, e);
    return 'Invalid Time';
  }
};

// CourseSummaryCard Component: Displays individual course enrollment details
const CourseSummaryCard = ({ enrollment }) => {
  // State to manage the visibility of upcoming and past classes sections
  const [showUpcoming, setShowUpcoming] = useState(false);
  const [showPast, setShowPast] = useState(false);

  // Destructure relevant data from the enrollment prop
  const { course, teacherName, bookedClasses, completedClasses } = enrollment;
  const { title, description, nextClass, upcomingClasses, pastClasses } = course;

  return (
    // Main card container with beautiful modern styling, emphasizing a kid-friendly feel
    <div className="bg-gradient-to-br from-purple-50 to-pink-100 p-6 rounded-3xl shadow-2xl hover:shadow-pink-300 transition-all duration-300 transform hover:-translate-y-2 border-2 border-purple-200">
      {/* Course Title and Teacher Info */}
      <div className="flex items-center mb-4">
        {/* Playful Icon for the course */}
        <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-4 rounded-full text-white flex items-center justify-center mr-4 shadow-md">
          {/* SVG for a book/graduation cap icon for a learning theme */}
          <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z"></path>
            <path d="M12 6a2 2 0 11-4 0 2 2 0 014 0z"></path>
          </svg>
        </div>
        <div>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-1 leading-tight">{title}</h2>
          <p className="text-md text-purple-700 font-semibold">Taught by: {teacherName}</p>
        </div>
      </div>

      {/* Course Description */}
      <p className="text-gray-700 mb-5 text-base">{description}</p>

      {/* Booked and Completed Classes Counts - Larger, more prominent */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div className="bg-white p-5 rounded-2xl shadow-lg border border-purple-200 flex flex-col items-center justify-center text-center">
          <span className="text-gray-700 font-medium text-lg mb-1">Booked Classes:</span>
          <span className="text-indigo-600 font-bold text-4xl">{bookedClasses || 0}</span>
        </div>
        <div className="bg-white p-5 rounded-2xl shadow-lg border border-pink-200 flex flex-col items-center justify-center text-center">
          <span className="text-gray-700 font-medium text-lg mb-1">Completed Classes:</span>
          <span className="text-emerald-600 font-bold text-4xl">{completedClasses || 0}</span>
        </div>
      </div>

      {/* Next Class Information (conditionally rendered) */}
      {nextClass && (
        <div className="bg-white p-6 rounded-2xl shadow-xl border-2 border-indigo-300 mb-6 relative overflow-hidden">
            <div className="absolute -top-4 -right-4 bg-indigo-200 text-indigo-700 px-4 py-1 rounded-bl-xl text-sm font-bold transform rotate-12 origin-bottom-left">
                Next Up!
            </div>
          <h3 className="text-xl font-bold text-indigo-800 flex items-center mb-4">
            {/* SVG for a shining star/clock icon */}
            <svg className="w-6 h-6 mr-3 text-indigo-500 animate-pulse" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd"></path>
            </svg>
            Your Next Class:
          </h3>
          <p className="text-gray-800 font-semibold mb-2 text-lg">
            <span className="text-gray-600">Date:</span> {formatDate(nextClass.classDate)} <span className="font-normal text-indigo-700">at</span> {formatTime(nextClass.time)}
          </p>
          <p className="text-gray-800 font-semibold mb-3 text-lg">
            <span className="text-gray-600">Chapter:</span> {nextClass.chapterTitle}
          </p>
          {nextClass.topics && nextClass.topics.length > 0 && (
            <div className="mb-4 bg-indigo-50 p-4 rounded-xl border border-indigo-100">
              <span className="text-gray-600 font-semibold text-base mb-2 block">Topics for this Class:</span>
              <ul className="list-inside text-gray-700 text-base space-y-1">
                {nextClass.topics.map((topic, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-indigo-500 mr-2">&#10003;</span> {/* Checkmark icon */}
                    <div>
                      <span className="font-medium">{topic.title || 'Untitled Topic'}</span>
                      {topic.description && <span className="text-gray-500 ml-2 italic text-sm">({topic.description})</span>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* Join Meeting Button - More prominent and engaging */}
            <a
              href={nextClass.joinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-flex items-center justify-center px-6 py-3 border-0 text-lg font-bold rounded-full shadow-lg text-white bg-gradient-to-r from-emerald-400 to-teal-500 hover:from-emerald-500 hover:to-teal-600 focus:outline-none focus:ring-4 focus:ring-emerald-300 focus:ring-offset-2 transition-all duration-300 transform hover:scale-105"
            >
              {/* SVG for a direct "play" or "join" icon */}
              <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd"></path>
              </svg>
              Join Your Class Now!
            </a>
          
        </div>
      )}

      {/* Upcoming Classes Section (collapsible) - Fun, distinct button */}
      {upcomingClasses.length > 0 && (
        <div className="mb-6">
          <button
            onClick={() => setShowUpcoming(!showUpcoming)}
            className="w-full text-left py-4 px-6 bg-gradient-to-r from-blue-400 to-cyan-500 hover:from-blue-500 hover:to-cyan-600 text-white font-bold text-lg rounded-xl shadow-lg transition-all duration-300 flex justify-between items-center transform hover:scale-102"
          >
            <span className="flex items-center">
              {/* SVG for a rocket or future icon */}
              <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l.649-.182.001-.001-.459 1.139A1 1 0 0014 16.5V13a3 3 0 00-3-3H9a3 3 0 00-3 3v3.5a.5.5 0 00.5.5h4a.5.5 0 00.5-.5V14h1.5a.5.5 0 00.5-.5z"></path>
              </svg>
              Exciting Upcoming Classes ({upcomingClasses.length})
            </span>
            {/* Chevron icon for expand/collapse */}
            <svg
              className={`w-6 h-6 transform transition-transform duration-300 ${showUpcoming ? 'rotate-180' : ''}`}
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path>
            </svg>
          </button>
          {showUpcoming && (
            <div className="mt-4 space-y-4 p-5 bg-blue-50 rounded-2xl shadow-inner border border-blue-200">
              {upcomingClasses.map((cl) => (
                <div key={cl.classScheduleId} className="border-b-2 border-blue-100 pb-4 last:border-b-0">
                  <p className="text-gray-900 font-semibold text-lg">Class {cl.classNumber}: <span className="text-blue-700">{formatDate(cl.classDate)} - {formatTime(cl.time)}</span></p>
                  <p className="text-gray-800 text-base mt-1">Chapter: <span className="font-medium">{cl.chapterTitle}</span></p>
                  {cl.topics && cl.topics.length > 0 && (
                    <ul className="list-inside text-gray-700 text-sm pl-4 mt-2 space-y-1">
                      {cl.topics.map((topic, topicIndex) => (
                        <li key={topicIndex} className="flex items-center">
                            <span className="text-blue-400 mr-2">&bull;</span> {topic.title || 'Untitled Topic'}
                        </li>
                      ))}
                    </ul>
                  )}
                  {cl.joinUrl && (
                    <a href={cl.joinUrl} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700 text-md font-bold mt-3 inline-block transition-colors duration-200">
                      Join Class
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Past Classes Section (collapsible) - Softer color, still engaging */}
      {pastClasses.length > 0 && (
        <div>
          <button
            onClick={() => setShowPast(!showPast)}
            className="w-full text-left py-4 px-6 bg-gray-500 hover:bg-gray-600 text-white font-bold text-lg rounded-xl shadow-lg transition-all duration-300 flex justify-between items-center transform hover:scale-102"
          >
            <span className="flex items-center">
              {/* SVG for a checkmark or achievement icon */}
              <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
              </svg>
              Completed Classes ({pastClasses.length})
            </span>
            {/* Chevron icon for expand/collapse */}
            <svg
              className={`w-6 h-6 transform transition-transform duration-300 ${showPast ? 'rotate-180' : ''}`}
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path>
            </svg>
          </button>
          {showPast && (
            <div className="mt-4 space-y-4 p-5 bg-gray-50 rounded-2xl shadow-inner border border-gray-200">
              {pastClasses.map((cl) => (
                <div key={cl.classScheduleId} className="border-b-2 border-gray-100 pb-4 last:border-b-0">
                  <p className="text-gray-900 font-semibold text-lg">Class {cl.classNumber}: <span className="text-gray-600">{formatDate(cl.classDate)} - {formatTime(cl.time)}</span></p>
                  <p className="text-gray-800 text-base mt-1">Chapter: <span className="font-medium">{cl.chapterTitle}</span></p>
                  {cl.topics && cl.topics.length > 0 && (
                    <ul className="list-inside text-gray-700 text-sm pl-4 mt-2 space-y-1">
                      {cl.topics.map((topic, topicIndex) => (
                        <li key={topicIndex} className="flex items-center">
                            <span className="text-gray-400 mr-2">&bull;</span> {topic.title || 'Untitled Topic'}
                        </li>
                      ))}
                    </ul>
                  )}
                  {cl.zoomMeetingId && (
                    <span className="text-sm text-gray-500 mt-2 inline-block">
                      {/* <span className="font-semibold">Meeting ID:</span> {cl.zoomMeetingId} */}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Message if no classes found for this course */}
      {(!nextClass && upcomingClasses.length === 0 && pastClasses.length === 0) && (
        <div className="text-center text-gray-500 py-8 text-lg">
          <p>No classes found for this course yet. Check back soon!</p>
        </div>
      )}
    </div>
  );
};

// MyCourse Component: Fetches and displays all enrolled courses
const MyCourse = () => {
  const [enrollments, setEnrollments] = useState([]); // State to hold the fetched enrollments
  const [loading, setLoading] = useState(true);     // State for loading indicator
  const [error, setError] = useState(null);         // State for error messages

  // IMPORTANT: Replace with your actual API base URL.
  // In a real application, this would ideally come from environment variables or a configuration context.

  useEffect(() => {
    // Function to fetch enrolled courses from the API
    const fetchEnrolledCourses = async () => {
      try {
        setLoading(true); // Set loading to true before fetching
        setError(null);   // Clear any previous errors

        const token = localStorage.getItem('token'); // Get authentication token from localStorage
        if (!token) {
          setError("Authentication token not found. Please log in.");
          setLoading(false);
          return; // Stop execution if no token is found
        }

        // Make the API call to your backend endpoint
        const response = await fetch(`${baseUrl}api/students/enrolled-courses`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`, // Include the authorization token
          },
        });

        // Check if the response was successful
        if (!response.ok) {
          const errorData = await response.json();
          // Throw an error with the message from the API or a generic one
          throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }

        const data = await response.json(); // Parse the JSON response
        setEnrollments(data); // Update the enrollments state with fetched data
      } catch (err) {
        console.error("Error fetching enrolled courses:", err);
        setError(err.message || "Failed to fetch enrolled courses."); // Set error state
      } finally {
        setLoading(false); // Set loading to false after fetch completes (success or error)
      }
    };

    fetchEnrolledCourses(); // Call the fetch function when the component mounts
  }, [baseUrl]); // Dependency array: re-run effect if baseUrl changes

  // Render loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 font-sans p-6">
        <div className="flex flex-col items-center p-8 bg-white rounded-3xl shadow-xl">
          <div className="animate-bounce bg-blue-500 p-4 rounded-full mb-4">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path></svg>
          </div>
          <p className="text-xl text-gray-700 font-semibold">Gathering your exciting courses...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-pink-50 font-sans p-6">
        <div className="text-center p-8 bg-white rounded-3xl shadow-xl border-2 border-red-400">
          <h2 className="text-3xl font-bold text-red-700 mb-4">Oops! Something Went Wrong</h2>
          <p className="text-red-600 mb-6 text-lg">{error}</p>
          <button
            onClick={() => window.location.reload()} // Allow user to retry by reloading the page
            className="px-8 py-3 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-md text-lg font-semibold"
          >
            Try Again!
          </button>
        </div>
      </div>
    );
  }

  // Render state when no courses are enrolled
  if (enrollments.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 font-sans p-6">
        <div className="text-center p-8 bg-white rounded-3xl shadow-xl border-2 border-gray-200">
          <h2 className="text-3xl font-bold text-gray-700 mb-4">No Courses Yet!</h2>
          <p className="text-gray-600 text-lg mb-6">It looks like you haven't enrolled in any adventures yet. Time to explore!</p>
          {/* You could add a button here to navigate to a course catalog or suggest enrollment */}
          <button className="px-8 py-3 bg-indigo-500 text-white rounded-full hover:bg-indigo-600 transition-colors shadow-md text-lg font-semibold">
            Find New Courses!
          </button>
        </div>
      </div>
    );
  }

  // Render the list of enrolled courses
  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-purple-50 font-sans p-4 sm:p-8 lg:p-12">
      {/* Tailwind CSS CDN and Google Fonts (Inter) for styling */}
      <script src="https://cdn.tailwindcss.com"></script>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
          body { font-family: 'Inter', sans-serif; }
        `}
      </style>
      {/* Header section with a playful touch */}
      <header className="text-center mb-12">
        <h1 className="text-5xl sm:text-6xl font-extrabold text-gray-900 leading-tight">
          Your <span className="text-purple-600">Learning Adventures</span>
        </h1>
        <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
          Explore your classes, see what's next, and celebrate your progress!
        </p>
      </header>
      {/* Grid to display CourseSummaryCard components */}
      <div className="">
        {enrollments.map((enrollment) => (
          // Use courseId as key as it's unique per course enrollment
          <CourseSummaryCard key={enrollment.course.courseId} enrollment={enrollment} />
        ))}
      </div>
    </div>
  );
};

export default MyCourse;
